/**
 * @file AdminSettings.jsx
 * @description Admin page for managing application settings (API key, models)
 * @module AdminSettings
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Chip,
  Radio,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Visibility,
  VisibilityOff,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Key as KeyIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import {
  getSettings,
  updateSettings,
  testApiKey,
} from '../services/api';

/**
 * Model section component for managing a list of models
 */
function ModelSection({ title, subtitle, models, defaultModel, onModelsChange, onDefaultChange }) {
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingModel, setEditingModel] = useState(null);
  const [newModel, setNewModel] = useState({
    id: '',
    name: '',
    description: '',
    speed: 'Fast',
    cost: '$',
  });

  const handleAddModel = () => {
    if (!newModel.id || !newModel.name) return;

    // Check for duplicate
    if (models.some(m => m.id === newModel.id)) {
      return;
    }

    onModelsChange([...models, { ...newModel }]);
    setNewModel({ id: '', name: '', description: '', speed: 'Fast', cost: '$' });
    setAddDialogOpen(false);
  };

  const handleEditModel = (model) => {
    setEditingModel({ ...model });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = () => {
    if (!editingModel.id || !editingModel.name) return;

    const updated = models.map(m =>
      m.id === editingModel.originalId ? { ...editingModel, originalId: undefined } : m
    );
    onModelsChange(updated);

    // If the model ID changed and it was the default, update the default
    if (editingModel.originalId !== editingModel.id && editingModel.originalId === defaultModel) {
      onDefaultChange(editingModel.id);
    }

    setEditDialogOpen(false);
    setEditingModel(null);
  };

  const handleRemoveModel = (modelId) => {
    const updated = models.filter(m => m.id !== modelId);
    onModelsChange(updated);

    // If removing the default, clear it or set to first available
    if (modelId === defaultModel) {
      onDefaultChange(updated.length > 0 ? updated[0].id : '');
    }
  };

  const handleSetDefault = (modelId) => {
    onDefaultChange(modelId);
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {subtitle}
      </Typography>

      <List dense>
        {models.map((model) => (
          <ListItem
            key={model.id}
            sx={{
              bgcolor: model.id === defaultModel ? 'action.selected' : 'transparent',
              borderRadius: 1,
              mb: 0.5,
            }}
          >
            <Radio
              checked={model.id === defaultModel}
              onChange={() => handleSetDefault(model.id)}
              size="small"
            />
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1">{model.name}</Typography>
                  <Chip label={model.speed} size="small" variant="outlined" />
                  <Chip label={model.cost} size="small" color="primary" variant="outlined" />
                  {model.id === defaultModel && (
                    <Chip label="Default" size="small" color="success" />
                  )}
                </Box>
              }
              secondary={
                <Typography variant="caption" color="text.secondary">
                  {model.id} - {model.description}
                </Typography>
              }
            />
            <ListItemSecondaryAction>
              <Tooltip title="Edit model">
                <IconButton
                  onClick={() => handleEditModel({ ...model, originalId: model.id })}
                  size="small"
                  sx={{ mr: 0.5 }}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Remove model">
                <IconButton
                  edge="end"
                  onClick={() => handleRemoveModel(model.id)}
                  disabled={models.length === 1}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>

      <Button
        startIcon={<AddIcon />}
        onClick={() => setAddDialogOpen(true)}
        sx={{ mt: 1 }}
      >
        Add Model
      </Button>

      {/* Add Model Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Model</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Model ID"
              value={newModel.id}
              onChange={(e) => setNewModel({ ...newModel, id: e.target.value })}
              placeholder="e.g., openai/gpt-5.1"
              fullWidth
              required
              helperText="The OpenRouter model ID"
            />
            <TextField
              label="Display Name"
              value={newModel.name}
              onChange={(e) => setNewModel({ ...newModel, name: e.target.value })}
              placeholder="e.g., GPT-5.1"
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={newModel.description}
              onChange={(e) => setNewModel({ ...newModel, description: e.target.value })}
              placeholder="e.g., Latest GPT-5 series, highest accuracy"
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Speed</InputLabel>
              <Select
                value={newModel.speed}
                label="Speed"
                onChange={(e) => setNewModel({ ...newModel, speed: e.target.value })}
              >
                <MenuItem value="Very Fast">Very Fast</MenuItem>
                <MenuItem value="Fast">Fast</MenuItem>
                <MenuItem value="Standard">Standard</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Cost</InputLabel>
              <Select
                value={newModel.cost}
                label="Cost"
                onChange={(e) => setNewModel({ ...newModel, cost: e.target.value })}
              >
                <MenuItem value="$">$ (Low)</MenuItem>
                <MenuItem value="$$">$$ (Medium)</MenuItem>
                <MenuItem value="$$$">$$$ (High)</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddModel}
            variant="contained"
            disabled={!newModel.id || !newModel.name}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Model Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Model</DialogTitle>
        <DialogContent>
          {editingModel && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                label="Model ID"
                value={editingModel.id}
                onChange={(e) => setEditingModel({ ...editingModel, id: e.target.value })}
                placeholder="e.g., openai/gpt-5.1"
                fullWidth
                required
                helperText="The OpenRouter model ID"
              />
              <TextField
                label="Display Name"
                value={editingModel.name}
                onChange={(e) => setEditingModel({ ...editingModel, name: e.target.value })}
                placeholder="e.g., GPT-5.1"
                fullWidth
                required
              />
              <TextField
                label="Description"
                value={editingModel.description}
                onChange={(e) => setEditingModel({ ...editingModel, description: e.target.value })}
                placeholder="e.g., Latest GPT-5 series, highest accuracy"
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>Speed</InputLabel>
                <Select
                  value={editingModel.speed}
                  label="Speed"
                  onChange={(e) => setEditingModel({ ...editingModel, speed: e.target.value })}
                >
                  <MenuItem value="Very Fast">Very Fast</MenuItem>
                  <MenuItem value="Fast">Fast</MenuItem>
                  <MenuItem value="Standard">Standard</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Cost</InputLabel>
                <Select
                  value={editingModel.cost}
                  label="Cost"
                  onChange={(e) => setEditingModel({ ...editingModel, cost: e.target.value })}
                >
                  <MenuItem value="$">$ (Low)</MenuItem>
                  <MenuItem value="$$">$$ (Medium)</MenuItem>
                  <MenuItem value="$$$">$$$ (High)</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            disabled={!editingModel?.id || !editingModel?.name}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

/**
 * Admin settings management page
 *
 * Allows admins to configure:
 * - OpenRouter API key
 * - Pass 1 models (primary extraction)
 * - Pass 2 models (auto verification)
 * - Manual revalidation models
 *
 * @returns {React.ReactElement} Admin settings page
 */
function AdminSettings() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // API Key state
  const [apiKey, setApiKey] = useState('');
  const [apiKeyMasked, setApiKeyMasked] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [hasApiKey, setHasApiKey] = useState(false);
  const [testingKey, setTestingKey] = useState(false);
  const [keyTestResult, setKeyTestResult] = useState(null);

  // Model configurations
  const [pass1Models, setPass1Models] = useState([]);
  const [pass2Models, setPass2Models] = useState([]);
  const [manualRerunModels, setManualRerunModels] = useState([]);
  const [pass1Default, setPass1Default] = useState('');
  const [pass2Default, setPass2Default] = useState('');
  const [manualRerunDefault, setManualRerunDefault] = useState('');

  // Track if there are unsaved changes
  const [hasChanges, setHasChanges] = useState(false);

  // Load settings on mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await getSettings();
      const settings = response.data.settings;

      setHasApiKey(settings.hasApiKey);
      setApiKeyMasked(settings.apiKeyMasked || '');
      setPass1Models(settings.pass1Models || []);
      setPass2Models(settings.pass2Models || []);
      setManualRerunModels(settings.manualRerunModels || []);
      setPass1Default(settings.pass1Default || '');
      setPass2Default(settings.pass2Default || '');
      setManualRerunDefault(settings.manualRerunDefault || '');
      setHasChanges(false);
    } catch (err) {
      console.error('Failed to load settings:', err);
      setError('Failed to load settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTestApiKey = async () => {
    const keyToTest = apiKey || null;
    if (!keyToTest && !hasApiKey) {
      enqueueSnackbar('Please enter an API key to test', { variant: 'warning' });
      return;
    }

    try {
      setTestingKey(true);
      setKeyTestResult(null);

      // If user entered a new key, test that; otherwise test the stored one
      const response = await testApiKey(keyToTest || 'USE_STORED');

      if (response.data.valid) {
        setKeyTestResult({ valid: true });
        enqueueSnackbar('API key is valid!', { variant: 'success' });
      } else {
        setKeyTestResult({ valid: false, error: response.data.error });
        enqueueSnackbar(`API key invalid: ${response.data.error}`, { variant: 'error' });
      }
    } catch (err) {
      console.error('Failed to test API key:', err);
      setKeyTestResult({ valid: false, error: 'Test failed' });
      enqueueSnackbar('Failed to test API key', { variant: 'error' });
    } finally {
      setTestingKey(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      const updateData = {
        pass1Models,
        pass2Models,
        manualRerunModels,
        pass1Default,
        pass2Default,
        manualRerunDefault,
      };

      // Only include API key if user entered a new one
      if (apiKey) {
        updateData.apiKey = apiKey;
      }

      await updateSettings(updateData);

      enqueueSnackbar('Settings saved successfully!', { variant: 'success' });
      setHasChanges(false);
      setApiKey(''); // Clear the input after saving

      // Reload to get fresh masked key
      await loadSettings();
    } catch (err) {
      console.error('Failed to save settings:', err);
      setError('Failed to save settings. Please try again.');
      enqueueSnackbar('Failed to save settings', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Track changes
  const handleModelsChange = (setter) => (models) => {
    setter(models);
    setHasChanges(true);
  };

  const handleDefaultChange = (setter) => (value) => {
    setter(value);
    setHasChanges(true);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/')} sx={{ mr: 2 }}>
          <BackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          Settings
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* API Key Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <KeyIcon color="primary" />
          <Typography variant="h6">OpenRouter API Key</Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Your API key is used to authenticate with OpenRouter for AI processing.
          {hasApiKey && ' Current key is stored securely.'}
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <TextField
            label={hasApiKey ? 'New API Key (leave empty to keep current)' : 'API Key'}
            type={showApiKey ? 'text' : 'password'}
            value={apiKey}
            onChange={(e) => {
              setApiKey(e.target.value);
              setHasChanges(true);
              setKeyTestResult(null);
            }}
            placeholder={hasApiKey ? apiKeyMasked : 'sk-or-v1-...'}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowApiKey(!showApiKey)} edge="end">
                    {showApiKey ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="outlined"
            onClick={handleTestApiKey}
            disabled={testingKey || (!apiKey && !hasApiKey)}
            sx={{ minWidth: 100, height: 56 }}
          >
            {testingKey ? <CircularProgress size={20} /> : 'Test'}
          </Button>
        </Box>

        {keyTestResult && (
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            {keyTestResult.valid ? (
              <>
                <CheckIcon color="success" />
                <Typography color="success.main">API key is valid</Typography>
              </>
            ) : (
              <>
                <ErrorIcon color="error" />
                <Typography color="error.main">
                  Invalid: {keyTestResult.error}
                </Typography>
              </>
            )}
          </Box>
        )}

        {hasApiKey && !apiKey && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Current key: {apiKeyMasked}
          </Typography>
        )}
      </Paper>

      <Divider sx={{ my: 3 }} />

      {/* Pass 1 Models */}
      <ModelSection
        title="Pass 1 Models (Primary Extraction)"
        subtitle="Models used for initial statement extraction. The default model is used when processing starts."
        models={pass1Models}
        defaultModel={pass1Default}
        onModelsChange={handleModelsChange(setPass1Models)}
        onDefaultChange={handleDefaultChange(setPass1Default)}
      />

      {/* Pass 2 Models */}
      <ModelSection
        title="Pass 2 Models (Auto Verification)"
        subtitle="Models used for automatic verification when balance reconciliation fails on first pass."
        models={pass2Models}
        defaultModel={pass2Default}
        onModelsChange={handleModelsChange(setPass2Models)}
        onDefaultChange={handleDefaultChange(setPass2Default)}
      />

      {/* Manual Rerun Models */}
      <ModelSection
        title="Manual Revalidation Models"
        subtitle="Models available for manual rerun when you want to re-process a statement with a different model."
        models={manualRerunModels}
        defaultModel={manualRerunDefault}
        onModelsChange={handleModelsChange(setManualRerunModels)}
        onDefaultChange={handleDefaultChange(setManualRerunDefault)}
      />

      {/* Save Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button
          variant="contained"
          size="large"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving || !hasChanges}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>

      {hasChanges && (
        <Typography variant="caption" color="warning.main" sx={{ display: 'block', textAlign: 'right', mt: 1 }}>
          You have unsaved changes
        </Typography>
      )}
    </Box>
  );
}

export default AdminSettings;
